import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Loader2, Calendar, MapPin, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function TrackBooking() {
  const [bookingCode, setBookingCode] = useState('');
  const [email, setEmail] = useState('');
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    api.get('/public/settings').then(res => setSettings(res.data)).catch(console.error);
  }, []);

  // Helper per ottenere testi CMS con fallback
  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!bookingCode || !email) {
      toast.error(t('msg_error_required', 'Inserisci codice prenotazione e email'));
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await api.get(`/bookings/track/${bookingCode}?email=${encodeURIComponent(email)}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Track error:', error);
      setBooking(null);
      if (error.response?.status === 404) {
        toast.error(t('msg_error_booking_not_found', 'Prenotazione non trovata. Verifica i dati inseriti.'));
      } else {
        toast.error(t('msg_error_generic', 'Errore nella ricerca'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      nuova: { label: 'Nuova', icon: AlertCircle, color: 'text-blue-500', bg: 'bg-blue-50' },
      richiesta: { label: 'In Attesa', icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50' },
      in_valutazione: { label: 'In Valutazione', icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50' },
      confermata: { label: 'Confermata', icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
      rifiutata: { label: 'Rifiutata', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
      completata: { label: 'Completata', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
      annullata: { label: 'Annullata', icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-50' }
    };
    return statusMap[status] || statusMap.richiesta;
  };

  return (
    <div className="pt-32 pb-24" data-testid="track-booking-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 lg:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
            {t('track_page_label', 'Traccia')}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            {t('page_track_title', 'La Tua Prenotazione')}
          </h1>
          <p className="text-muted-foreground">
            {t('page_track_subtitle', 'Inserisci il codice di prenotazione e l\'email per verificare lo stato della tua richiesta.')}
          </p>
        </motion.div>
      </div>

      {/* Search Form */}
      <div className="max-w-xl mx-auto px-6 lg:px-12 mb-12">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSearch}
          className="bg-card border border-border p-8"
          data-testid="track-form"
        >
          <div className="mb-6">
            <Label className="mb-3 block">{t('track_code_label', 'Codice Prenotazione')}</Label>
            <Input
              value={bookingCode}
              onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
              placeholder={t('track_code_placeholder', 'Es: ABC12345')}
              className="font-mono uppercase"
              data-testid="booking-code-input"
            />
          </div>

          <div className="mb-6">
            <Label className="mb-3 block">{t('form_email', 'Email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('form_email_placeholder', 'La tua email')}
              data-testid="track-email-input"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="track-submit-btn">
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                {t('btn_searching', 'Ricerca...')}
              </>
            ) : (
              <>
                <Search size={16} className="mr-2" />
                {t('btn_search_booking', 'Cerca Prenotazione')}
              </>
            )}
          </Button>
        </motion.form>
      </div>

      {/* Results */}
      {searched && (
        <div className="max-w-xl mx-auto px-6 lg:px-12">
          {booking ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border p-8"
              data-testid="booking-result"
            >
              {/* Status Badge */}
              {(() => {
                const statusInfo = getStatusInfo(booking.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 ${statusInfo.bg} ${statusInfo.color} mb-6`}>
                    <StatusIcon size={18} />
                    <span className="font-medium">{statusInfo.label}</span>
                  </div>
                );
              })()}

              <h2 className="font-heading text-2xl mb-6">{t('track_details_title', 'Dettagli Prenotazione')}</h2>
              
              <dl className="space-y-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground">{t('booking_code', 'Codice')}</dt>
                  <dd className="font-mono font-medium">{booking.booking_code}</dd>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground">{t('form_service', 'Servizio')}</dt>
                  <dd className="font-medium">{booking.service_type}</dd>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground flex items-center gap-2">
                    <Calendar size={16} />
                    {t('form_date', 'Data')}
                  </dt>
                  <dd className="font-medium">{booking.preferred_date}</dd>
                </div>
                {booking.time_slot && (
                  <div className="flex justify-between py-3 border-b border-border">
                    <dt className="text-muted-foreground flex items-center gap-2">
                      <Clock size={16} />
                      {t('booking_time_slot', 'Orario')}
                    </dt>
                    <dd className="font-medium">{booking.time_slot}</dd>
                  </div>
                )}
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground flex items-center gap-2">
                    <MapPin size={16} />
                    {t('booking_location', 'Luogo')}
                  </dt>
                  <dd className="font-medium">{booking.location}</dd>
                </div>
                <div className="flex justify-between py-3">
                  <dt className="text-muted-foreground">{t('track_created_at', 'Creata il')}</dt>
                  <dd className="text-sm">{new Date(booking.created_at).toLocaleDateString('it-IT')}</dd>
                </div>
              </dl>

              {(booking.status === 'richiesta' || booking.status === 'in_valutazione') && (
                <p className="mt-6 text-sm text-muted-foreground bg-muted/50 p-4">
                  {t('track_pending_message', 'La tua richiesta è in fase di valutazione. Riceverai un\'email quando sarà confermata.')}
                </p>
              )}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 text-muted-foreground"
              data-testid="no-booking-found"
            >
              <Search size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('track_not_found', 'Nessuna prenotazione trovata con questi dati.')}</p>
              <p className="text-sm mt-2">{t('track_verify_data', 'Verifica di aver inserito correttamente il codice e l\'email.')}</p>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
